// Branded types for type safety
export type Calories = number & { readonly __brand: 'Calories' }
export type Grams = number & { readonly __brand: 'Grams' }
export type Milligrams = number & { readonly __brand: 'Milligrams' }
export type Milliliters = number & { readonly __brand: 'Milliliters' }
export type Micrograms = number & { readonly __brand: 'Micrograms' }
export type InternationalUnits = number & { readonly __brand: 'InternationalUnits' }

// Unit types - standardized to simple forms only
export type MassUnit = 'g'
export type VolumeUnit = 'ml'
export type CountUnit = 'piece'
export type Unit = MassUnit | VolumeUnit | CountUnit

// Nutritional value structure
export interface NutritionalValue {
  readonly calories: Calories
  readonly carbs: Grams
  readonly fat: Grams
  readonly protein: Grams
  readonly cholesterol: Milligrams
  readonly sodium: Milligrams
  readonly sugar: Grams
  readonly fiber: Grams
}

// Quantity with unit
export interface Quantity {
  readonly amount: number
  readonly unit: Unit
}

// Standardized quantity after unit conversion
export interface StandardizedQuantity {
  readonly amount: number
  readonly unit: Unit
}

// Food categories based on the categorization document
export type FoodCategory =
  | 'Fruits and Vegetables'
  | 'Legumes'
  | 'Whole Grains'
  | 'Nuts and Seeds'
  | 'Root Vegetables'
  | 'Herbs and Spices'
  | 'Meat, Poultry, Fish & Seafood'
  | 'Dairy & Eggs'
  | 'Fats, Oils & Spreads'
  | 'Processed Foods & Snacks'
  | 'Sweets, Sugars & Desserts'
  | 'Supplements & Protein Powders'
  | 'Beverages'
  | 'Unknown'

// Food item structure
export interface FoodItem {
  readonly name: string
  readonly quantity: Quantity
  readonly nutrition: NutritionalValue
  readonly category: FoodCategory
}

// Meal categories
export type MealCategory = 'Breakfast' | 'Lunch' | 'Dinner' | 'Late meal'

// Meal structure
export interface Meal {
  readonly category: string // Accepts MealCategory or TimeRange (defined later)
  readonly foods: readonly FoodItem[]
}

// Daily nutrition entry
export interface DailyNutrition {
  readonly date: Date
  readonly meals: readonly Meal[]
  readonly totals: NutritionalValue
}

// Helper type for parsing
export interface ParsedFoodLine {
  readonly foodDescription: string
  readonly nutritionalValues: readonly string[]
}

// Period filtering types
export type TimePeriod = 'week' | 'month' | '3months' | '6months' | 'year' | 'custom'

// Product statistics for table display
export interface ProductStatistics {
  readonly product: string
  readonly totalCalories: Calories
  readonly totalWeight: Grams
  readonly totalVolume: Milliliters
  readonly totalPieces: number
  readonly frequency: number
  readonly avgPortion: number
  readonly avgPortionUnit: Unit
  readonly proteins: Grams
  readonly fats: Grams
  readonly carbohydrates: Grams
  readonly fiber: Grams
}

// Nutrition data collection for statistics
export interface NutritionDataCollection {
  readonly entries: readonly DailyNutrition[]
  readonly dateRange: {
    readonly start: Date
    readonly end: Date
  }
}

// Filter options for nutrition statistics
export interface NutritionFilter {
  readonly period: TimePeriod
  readonly startDate?: Date
  readonly endDate?: Date
}

// Time range types for hourly calorie distribution
export type TimeRange = '00-04' | '04-08' | '08-12' | '12-16' | '16-20' | '20-24'

/**
 * All valid time ranges as an array
 */
export const ALL_TIME_RANGES: readonly TimeRange[] = [
  '00-04',
  '04-08',
  '08-12',
  '12-16',
  '16-20',
  '20-24',
] as const

/**
 * Meal category input type that supports both traditional meal names
 * and direct time range values for backward compatibility
 */
export type MealCategoryInput = MealCategory | TimeRange

// Time-based calorie distribution for a single day
export interface TimeBasedCalories {
  readonly date: Date
  readonly timeRanges: Record<TimeRange, Calories>
  readonly totalCalories: Calories
}

// Weekly nutrition summary for charts
export interface WeeklyNutritionSummary {
  readonly weekStart: Date
  readonly weekEnd: Date
  readonly dailySummaries: readonly DailyNutritionSummary[]
  readonly weeklyAverages: NutritionalValue
  readonly weeklyTotals: NutritionalValue
  readonly timeBasedCalories: readonly TimeBasedCalories[]
}

// Daily nutrition summary for charts
export interface DailyNutritionSummary {
  readonly date: Date
  readonly dayOfWeek: string
  readonly nutrition: NutritionalValue
  readonly macroPercentages: {
    readonly carbsPercent: number
    readonly fatPercent: number
    readonly proteinPercent: number
  }
  readonly timeBasedCalories: Record<TimeRange, Calories>
}

// Nutrition goals for comparison
export interface NutritionGoals {
  readonly dailyCalories: Calories
  readonly macroTargets: {
    readonly carbsPercent: number
    readonly fatPercent: number
    readonly proteinPercent: number
  }
}

// Chart data types for Recharts
export interface MacroChartData {
  readonly day: string
  readonly carbs: number
  readonly fat: number
  readonly protein: number
  readonly carbsCalories: number
  readonly fatCalories: number
  readonly proteinCalories: number
}

export interface TimeBasedChartData {
  readonly day: string
  readonly '00-04': number
  readonly '04-08': number
  readonly '08-12': number
  readonly '12-16': number
  readonly '16-20': number
  readonly '20-24': number
  readonly total: number
}

// Fiber-rich food categories (subset of all food categories)
export type FiberRichCategory =
  | 'Fruits and Vegetables'
  | 'Legumes'
  | 'Whole Grains'
  | 'Nuts and Seeds'
  | 'Root Vegetables'
  | 'Herbs and Spices'

// Daily fiber consumption data
export interface DailyFiberConsumption {
  readonly date: Date
  readonly dayOfWeek: string
  readonly totalFiber: Grams
  readonly fiberRichFoodCounts: Record<FiberRichCategory, number>
  readonly totalFiberRichFoods: number
}

// Fiber chart data for Recharts
export interface FiberChartData {
  readonly day: string
  readonly fiber: number
  readonly fiberRichFoods: number
  readonly fruitsVegetables: number
  readonly legumes: number
  readonly wholeGrains: number
  readonly nutsSeeds: number
  readonly rootVegetables: number
  readonly herbsSpices: number
}

// Weekly fiber summary
export interface WeeklyFiberSummary {
  readonly weekStart: Date
  readonly weekEnd: Date
  readonly dailyFiberConsumption: readonly DailyFiberConsumption[]
  readonly weeklyAverageFiber: Grams
  readonly weeklyTotalFiber: Grams
  readonly weeklyAverageFiberRichFoods: number
  readonly weeklyTotalFiberRichFoods: number
}

// Fiber source analysis for unique tracking
export interface FiberSourceAnalysis {
  readonly fiberFromRichCategories: Grams
  readonly fiberFromOtherFoods: Grams
  readonly uniqueFiberRichSources: readonly string[]
  readonly uniqueOtherFiberSources: readonly string[]
  readonly totalUniqueSources: number
}

// Daily fiber source analysis
export interface DailyFiberSourceAnalysis {
  readonly date: Date
  readonly dayOfWeek: string
  readonly analysis: FiberSourceAnalysis
}

// Weekly fiber source summary
export interface WeeklyFiberSourceSummary {
  readonly weekStart: Date
  readonly weekEnd: Date
  readonly dailyAnalysis: readonly DailyFiberSourceAnalysis[]
  readonly weeklyAnalysis: FiberSourceAnalysis
  readonly goalProgress: number // percentage toward 30 unique sources
}

/**
 * Type-safe constructors for branded types
 */

/**
 * Creates a branded Calories type for type safety.
 * @param value - Numeric value representing calories
 * @returns Branded Calories type
 */
export const createCalories = (value: number): Calories => value as Calories

/**
 * Creates a branded Grams type for type safety.
 * @param value - Numeric value representing grams
 * @returns Branded Grams type
 */
export const createGrams = (value: number): Grams => value as Grams

/**
 * Creates a branded Milligrams type for type safety.
 * @param value - Numeric value representing milligrams
 * @returns Branded Milligrams type
 */
export const createMilligrams = (value: number): Milligrams => value as Milligrams

/**
 * Creates a branded Milliliters type for type safety.
 * @param value - Numeric value representing milliliters
 * @returns Branded Milliliters type
 */
export const createMilliliters = (value: number): Milliliters => value as Milliliters

/**
 * Type guard to validate if an unknown value is a valid NutritionalValue.
 *
 * @param value - Unknown value to validate
 * @returns True if value is a valid NutritionalValue with non-negative values
 *
 * @example
 * ```typescript
 * if (isValidNutritionalValue(data)) {
 *   // TypeScript knows data is NutritionalValue
 *   console.log(data.calories)
 * }
 * ```
 */
export const isValidNutritionalValue = (value: unknown): value is NutritionalValue => {
  if (typeof value !== 'object' || value === null) return false

  const nutrition = value as Record<string, unknown>
  return (
    typeof nutrition.calories === 'number' &&
    typeof nutrition.carbs === 'number' &&
    typeof nutrition.fat === 'number' &&
    typeof nutrition.protein === 'number' &&
    typeof nutrition.cholesterol === 'number' &&
    typeof nutrition.sodium === 'number' &&
    typeof nutrition.sugar === 'number' &&
    typeof nutrition.fiber === 'number' &&
    nutrition.calories >= 0 &&
    nutrition.carbs >= 0 &&
    nutrition.fat >= 0 &&
    nutrition.protein >= 0 &&
    nutrition.cholesterol >= 0 &&
    nutrition.sodium >= 0 &&
    nutrition.sugar >= 0 &&
    nutrition.fiber >= 0
  )
}

/**
 * Creates default nutrition goals.
 * @returns Default nutrition goals with common macro targets
 */
export const createDefaultNutritionGoals = (): NutritionGoals => ({
  dailyCalories: createCalories(2000),
  macroTargets: {
    carbsPercent: 55,
    fatPercent: 25,
    proteinPercent: 20,
  },
})

/**
 * Calculates macro percentages from nutritional values.
 * @param nutrition - Nutritional values to calculate percentages from
 * @returns Object with carbs, fat, and protein percentages
 */
export const calculateMacroPercentages = (
  nutrition: NutritionalValue
): {
  carbsPercent: number
  fatPercent: number
  proteinPercent: number
} => {
  // Convert macros to calories: carbs=4cal/g, protein=4cal/g, fat=9cal/g
  const carbCalories = nutrition.carbs * 4
  const proteinCalories = nutrition.protein * 4
  const fatCalories = nutrition.fat * 9
  const totalMacroCalories = carbCalories + proteinCalories + fatCalories

  if (totalMacroCalories === 0) {
    return { carbsPercent: 0, fatPercent: 0, proteinPercent: 0 }
  }

  return {
    carbsPercent: Math.round((carbCalories / totalMacroCalories) * 100 * 10) / 10,
    fatPercent: Math.round((fatCalories / totalMacroCalories) * 100 * 10) / 10,
    proteinPercent: Math.round((proteinCalories / totalMacroCalories) * 100 * 10) / 10,
  }
}

/**
 * Gets the time range for a given hour (0-23).
 * @param hour - Hour of the day (0-23)
 * @returns TimeRange that the hour falls into
 */
export const getTimeRangeFromHour = (hour: number): TimeRange => {
  if (hour >= 0 && hour < 4) return '00-04'
  if (hour >= 4 && hour < 8) return '04-08'
  if (hour >= 8 && hour < 12) return '08-12'
  if (hour >= 12 && hour < 16) return '12-16'
  if (hour >= 16 && hour < 20) return '16-20'
  return '20-24'
}

/**
 * Creates an empty time-based calories record.
 * @returns Record with all time ranges set to 0 calories
 */
export const createEmptyTimeBasedCalories = (): Record<TimeRange, Calories> => ({
  '00-04': createCalories(0),
  '04-08': createCalories(0),
  '08-12': createCalories(0),
  '12-16': createCalories(0),
  '16-20': createCalories(0),
  '20-24': createCalories(0),
})

// USDA Food Database Types

export type USDAUnit = 'g' | 'mg' | 'µg' | 'kcal' | 'IU'
export type DerivationMethod = 'Analytical' | 'Calculated' | 'Summed'

export interface USDANutrientValue {
  readonly averageAmount: number
  readonly unit: USDAUnit
  readonly derivationMethod: DerivationMethod
  readonly sampleCount?: number
  readonly sampleDescription?: string
  readonly minValue?: number
  readonly maxValue?: number
  readonly medianValue?: number
  readonly footnote?: string
  readonly initialYearAcquired?: number
}

export interface USDAProximates {
  readonly water: USDANutrientValue
  readonly energyAtwater: USDANutrientValue
  readonly energyAtwaterSpecific?: USDANutrientValue
  readonly protein: USDANutrientValue
  readonly totalLipidFat: USDANutrientValue
  readonly totalFatNLEA?: USDANutrientValue
  readonly ash: USDANutrientValue
  readonly carbohydratesByDifference: USDANutrientValue
}

export interface USDAMinerals {
  readonly calcium: USDANutrientValue
  readonly iron: USDANutrientValue
  readonly magnesium: USDANutrientValue
  readonly phosphorus: USDANutrientValue
  readonly potassium: USDANutrientValue
  readonly sodium: USDANutrientValue
  readonly zinc: USDANutrientValue
  readonly copper: USDANutrientValue
  readonly manganese: USDANutrientValue
  readonly selenium: USDANutrientValue
}

export interface USDAVitamins {
  readonly thiamin: USDANutrientValue
  readonly riboflavin: USDANutrientValue
  readonly niacin: USDANutrientValue
  readonly pantothenicAcid?: USDANutrientValue
  readonly vitaminB6: USDANutrientValue
  readonly cholineTotal?: USDANutrientValue
  readonly cholineFree?: USDANutrientValue
  readonly cholineFromPhosphocholine?: USDANutrientValue
  readonly cholineFromPhosphotidylCholine?: USDANutrientValue
  readonly cholineFromGlycerophosphocholine?: USDANutrientValue
  readonly cholineFromSphingomyelin?: USDANutrientValue
  readonly betaine?: USDANutrientValue
  readonly vitaminB12: USDANutrientValue
  readonly vitaminEAlphaTocopherol?: USDANutrientValue
  readonly tocopherolBeta?: USDANutrientValue
  readonly tocopherolGamma?: USDANutrientValue
  readonly tocopherolDelta?: USDANutrientValue
  readonly tocotrienolAlpha?: USDANutrientValue
  readonly tocotrienolBeta?: USDANutrientValue
  readonly tocotrienolGamma?: USDANutrientValue
  readonly tocotrienolDelta?: USDANutrientValue
  readonly vitaminDInternationalUnits?: USDANutrientValue
  readonly vitaminDTotal?: USDANutrientValue
  readonly vitaminD2?: USDANutrientValue
  readonly vitaminD3?: USDANutrientValue
  readonly hydroxycholecalciferol?: USDANutrientValue
}

export interface USDALipids {
  readonly fattyAcidsTotalSaturated: USDANutrientValue
  readonly sfa100?: USDANutrientValue
  readonly sfa120?: USDANutrientValue
  readonly sfa140: USDANutrientValue
  readonly sfa150?: USDANutrientValue
  readonly sfa160: USDANutrientValue
  readonly sfa170?: USDANutrientValue
  readonly sfa180: USDANutrientValue
  readonly sfa200?: USDANutrientValue
  readonly sfa240?: USDANutrientValue
  readonly fattyAcidsTotalMonounsaturated: USDANutrientValue
  readonly mufa141c?: USDANutrientValue
  readonly mufa161c: USDANutrientValue
  readonly mufa171?: USDANutrientValue
  readonly mufa171c?: USDANutrientValue
  readonly mufa181c: USDANutrientValue
  readonly mufa201?: USDANutrientValue
  readonly mufa201c?: USDANutrientValue
  readonly fattyAcidsTotalPolyunsaturated: USDANutrientValue
  readonly pufa182c?: USDANutrientValue
  readonly pufa182n6cc: USDANutrientValue
  readonly pufa182CLAs?: USDANutrientValue
  readonly pufa183c?: USDANutrientValue
  readonly pufa183n3ccc: USDANutrientValue
  readonly pufa202c?: USDANutrientValue
  readonly pufa202n6cc?: USDANutrientValue
  readonly pufa203c?: USDANutrientValue
  readonly pufa203n6?: USDANutrientValue
  readonly pufa204: USDANutrientValue
  readonly pufa204c?: USDANutrientValue
  readonly pufa205c?: USDANutrientValue
  readonly pufa205n3EPA: USDANutrientValue
  readonly pufa225c?: USDANutrientValue
  readonly pufa225n3DPA: USDANutrientValue
  readonly pufa226c?: USDANutrientValue
  readonly pufa226n3DHA: USDANutrientValue
  readonly fattyAcidsTotalTrans: USDANutrientValue
  readonly fattyAcidsTotalTransMonoenoic?: USDANutrientValue
  readonly tfa161t?: USDANutrientValue
  readonly tfa181t: USDANutrientValue
  readonly fattyAcidsTotalTransDienoic?: USDANutrientValue
  readonly tfa182tNotFurtherDefined?: USDANutrientValue
  readonly cholesterol: USDANutrientValue
}

export interface USDAFoodData {
  readonly id: string
  readonly name: string
  readonly description: string
  readonly proximates: USDAProximates
  readonly minerals: USDAMinerals
  readonly vitamins: USDAVitamins
  readonly lipids: USDALipids
}

/**
 * Creates a branded Micrograms type for type safety.
 * @param value - Numeric value representing micrograms
 * @returns Branded Micrograms type
 */
export const createMicrograms = (value: number): Micrograms => value as Micrograms

/**
 * Creates a branded InternationalUnits type for type safety.
 * @param value - Numeric value representing international units
 * @returns Branded InternationalUnits type
 */
export const createInternationalUnits = (value: number): InternationalUnits =>
  value as InternationalUnits

// Simplified nutrition data types for table display
export interface SimplifiedNutrientData {
  readonly name: string
  readonly averageAmount: number
  readonly unit: string
}

export interface NutritionCategory {
  readonly name: string
  readonly nutrients: readonly SimplifiedNutrientData[]
}

export interface SimplifiedNutritionDatabase {
  readonly categories: readonly NutritionCategory[]
}

export type SimplifiedNutritionData = readonly SimplifiedNutrientData[]
